import { Navigate } from "react-router-dom";

import { useMe } from "../../auth/useMe";

/**
 * Full operating guide for the superadmin. Routed at /app/admin/guide.
 *
 * Non-superadmins are redirected away. The page is print-friendly — clicking
 * "Download as PDF" calls window.print(), and the @media print CSS in
 * index.css strips the sidebar / chrome so the print dialog produces a clean
 * document the admin can save as PDF.
 */
export function SuperadminGuide() {
  const { isSuperadmin } = useMe();
  if (!isSuperadmin) return <Navigate to="/app" replace />;

  return (
    <div className="guide">
      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>Superadmin Guide</h1>
          <p className="muted" style={{ margin: 0 }}>
            How to set up and run the platform end-to-end. Use your browser's "Save as PDF" from the print dialog to keep an offline copy. Last updated 2026-05-09.
          </p>
        </div>
        <button onClick={() => window.print()}>Download as PDF</button>
      </div>

      <article className="guide-doc">
        <h1 className="guide-title">Operations Platform — Superadmin Guide</h1>
        <p className="guide-subtitle">
          The complete walk-through for setting up the territory hierarchy, granting access,
          running operations, and keeping the system safe.
        </p>

        <h2>What's new since the first release</h2>
        <ul>
          <li><strong>Sidebar split.</strong> <em>Reports</em> at top (read / aggregate / dashboards / map / audit). <em>Operations</em> below it — pure capture wizards (Capture Case, Capture Intel, Capture POI, Assign Task, Capture Performance, Capture Popular Persons). Groups are collapsible.</li>
          <li><strong>Capture wizards.</strong> Every capture flow is a multi-step wizard with a progress bar, validation, and a final review step. Same UX as the New User wizard.</li>
          <li><strong>Reports.</strong> At-a-glance summary callouts ("4 critical intel items unredacted"). Charts have ⓘ tooltips explaining what each means. 14-day trend lines for every category.</li>
          <li><strong>Map.</strong> OpenStreetMap-backed view, bounded to Zambia. Pick a data source as the primary tab (Cases / Intel / POI / Tasks / Performance / Popularity), narrow with severity / threat / priority chips, and choose between Streets / Terrain / Satellite tiles. Click any bubble to see the underlying records.</li>
          <li><strong>People.</strong> Community contacts attached to a ward — separate from <em>Users</em>, no sign-in account, no permissions. Encrypted PII at rest.</li>
          <li><strong>Organogram.</strong> Visualises the reports-to chain across all users, built from each assignment's "Reports to" field.</li>
          <li><strong>Hierarchy</strong> shows constituencies / wards / branches as 4-per-row cards with drill-down screens, plus optional lat/lng to plot on the Map.</li>
          <li><strong>Coded tasks.</strong> The recipient sees only a code (e.g. <code>TSK-A1B2</code>); the creator brief them on the details out-of-band.</li>
          <li><strong>Strong passwords.</strong> 12 chars min, must contain a letter, digit, and symbol; cannot include the user's email or name.</li>
          <li><strong>Encrypted user records.</strong> Names + phone numbers on the <code>users</code> collection are now AES-256-GCM encrypted at rest, alongside cases / intel / POI / tasks / performance / popularity / people.</li>
          <li><strong>Seed dummy data.</strong> Home page (superadmin only) has a button that populates a realistic intelligence-flavoured starter dataset, including the Zambia coordinates for the Map and rich POI profiles.</li>
        </ul>

        <h2>1. The mental model</h2>
        <p>
          The platform has three layers. You set them up in this order, top to bottom:
        </p>
        <ol>
          <li>
            <strong>Territory hierarchy</strong> — Constituency → Ward → Branch. Every operational
            record (case, intel, POI, performance report, popularity report, task) belongs to one
            branch. The hierarchy is permanent — records reference it forever, so you can rename
            but not delete entries that have children.
          </li>
          <li>
            <strong>Access control (RBAC)</strong> — three pieces work together:
            <ul>
              <li><strong>Permissions</strong> are tiny verbs (<code>case.read</code>, <code>intel.publish</code>).
                Permissions are seeded by the system; you don't create new ones.</li>
              <li><strong>Roles</strong> bundle permissions ("Intelligence Officer" = <code>intel.publish</code> +
                <code>case.read</code> + …).</li>
              <li><strong>Positions</strong> are job titles for the org chart ("Branch Manager"). They grant
                <em> nothing</em> on their own — they exist for reporting lines and labels.</li>
              <li><strong>Assignments</strong> tie a user to a role at a scope (constituency / ward / branch).
                A user can hold many roles at many scopes. They live <em>inside</em> the user record — you manage
                them on the Users page via <strong>Edit access</strong>.</li>
            </ul>
          </li>
          <li>
            <strong>Operations</strong> — the actual work: cases, intel feed, POI watchlist, performance
            and popularity reports, tasks, dashboards. What each user sees here is determined by the
            roles + scopes you assigned in step 2.
          </li>
        </ol>

        <h2>2. First-time setup, in order</h2>

        <h3>2.1 Sign in as superadmin</h3>
        <p>
          Go to <code>https://winnersden-55816.web.app/portal</code> and sign in with the superadmin
          account. Once signed in, the Operations app loads at <code>/app</code>. Bookmark
          <code>/app</code>; the landing page at <code>/</code> is a decoy.
        </p>

        <h3>2.2 Build the territory hierarchy</h3>
        <p>
          Open <strong>Setup → Hierarchy</strong>. Add records in order:
        </p>
        <ol>
          <li>
            <strong>Constituency</strong> — give it a name (e.g. "Mansa Central") and a short code
            (e.g. "MNS-CTR"). The code box auto-slugifies anything you type — spaces become
            hyphens and special characters drop. The preview shows exactly what will be saved.
          </li>
          <li>
            <strong>Ward</strong> — click "Show wards" on the constituency, then add wards in the
            same way.
          </li>
          <li>
            <strong>Branch</strong> — open a ward, then add branches.
          </li>
        </ol>
        <p>
          You can't create a ward without a constituency, or a branch without a ward. The system
          enforces the chain. Records here are append-only by design — you only see Delete buttons
          because you're a superadmin, and the API still refuses to delete a parent that has
          children. Every delete is audit-logged.
        </p>

        <h3>2.3 Decide on roles</h3>
        <p>
          Go to <strong>Setup → Roles</strong>. The system ships with a few system roles
          (read-only — you can't edit them). Click <strong>+ Create role</strong> for each role
          you need. Examples:
        </p>
        <ul>
          <li>
            <strong>Intelligence Officer</strong> — ticks: <code>intel.publish</code>,
            <code>intel.read</code>, <code>case.read</code>, <code>poi.read</code>,
            <code>task.create</code> (when senior), <code>performance.create</code>,
            <code>popularity.create</code>.
          </li>
          <li>
            <strong>Branch Manager</strong> — Intelligence Officer permissions plus
            <code>case.create</code>, <code>case.update</code>, <code>poi.create</code>,
            <code>poi.update</code>, <code>task.create</code>.
          </li>
          <li>
            <strong>Investigator</strong> — <code>case.create</code>, <code>case.update</code>,
            <code>case.read_classified</code> (only if cleared), <code>pii.read</code> if they
            need to see ID numbers and phone numbers.
          </li>
          <li>
            <strong>Auditor</strong> — <code>audit.read</code> only. Read-only oversight.
          </li>
        </ul>
        <p>
          When ticking, the box hint tells you what each resource controls. Be deliberate:
          <code>report</code> means <em>dashboards and CSV exports</em>, not "investigation
          reports". <code>pii.read</code> reveals masked names, ID numbers, and phone numbers
          across every screen — only grant it to people who need it.
        </p>

        <h3>2.4 Decide on positions (optional but recommended)</h3>
        <p>
          Go to <strong>Setup → Positions</strong>. Add the job titles your org uses. Pick a
          scope level (Constituency / Ward / Branch) so the system knows where in the hierarchy
          this title sits. Positions don't grant any permission — they're labels and reporting
          lines.
        </p>

        <h3>2.5 Create the people</h3>
        <p>
          Go to <strong>Setup → Users → + Create user</strong>. Enter the user's full name,
          email, phone, and an initial password. The user signs in with that password and can
          change it via the password reset link if you generate one.
        </p>
        <p>
          The user exists but cannot do anything yet. They have zero roles and zero scope.
        </p>

        <h3>2.6 Assign roles + scope</h3>
        <p>
          The first role + scope is set during step 2.5 (the create-user wizard). To add or revoke
          assignments later, go to <strong>Setup → Users</strong> and click <strong>Edit access</strong>
          on the user's row. The picker lets you choose:
        </p>
        <ul>
          <li>The user.</li>
          <li>The role.</li>
          <li>The position (optional).</li>
          <li>The scope: a single constituency, ward, or branch the role applies to.</li>
          <li>Their reports-to (the user senior to them in the chain). The system uses this to
            stop juniors assigning tasks to seniors.</li>
          <li>Optional start/end dates for time-bounded access (e.g. an intern).</li>
        </ul>
        <p>
          A user can hold multiple assignments — for example, an Investigator role at a branch and
          a Branch Manager role at a different branch. The user's effective permissions are the
          union of every active assignment.
        </p>

        <h3>2.7 Capture community contacts (People)</h3>
        <p>
          <strong>Setup → People.</strong> A separate concept from Users — these are community contacts
          (headmen, pastors, traders, informants) that you want to track operationally without giving them a
          platform account. They never sign in, hold no roles, and can't access any screen.
        </p>
        <p>
          A People record is attached to a single ward and carries: full name, position (free text e.g.
          "Headman", "Pastor"), ID number, phone, email, and free-form notes. Personal info is encrypted
          at rest with the same envelope as cases / POI / users.
        </p>

        <h3>2.8 See the org chart (Organogram)</h3>
        <p>
          <strong>Setup → Organogram.</strong> Visualises the reports-to chain across all users. Each
          person's "Reports to" field on their assignment (set under Users → Edit access) places them in the
          tree: anyone with no senior is a root; their direct reports nest under them. Cycles are detected
          and skipped with a warning.
        </p>
        <p>
          Organogram is generated live from assignment data — there's no separate org-chart store. Change
          someone's reports-to in their assignment and the chart updates on next view.
        </p>

        <h3>2.9 Sanity-check what they'll see</h3>
        <p>
          Open the user's row in <strong>Users</strong> and read the "N effective permission(s)"
          line. If it's zero, they haven't got an assignment yet.
        </p>

        <h2>3. Permissions reference</h2>
        <p>
          A permission is a single verb on a single resource — for example <code>case.read</code>
          (read cases) or <code>intel.publish</code> (publish intel items). Roles bundle these. When
          you tick a box in the role editor, you are granting one of these verbs.
        </p>
        <p>
          <strong>Least-privilege rule.</strong> When in doubt, leave the box unticked. You can
          always come back and grant more later — taking access away after someone has used it is
          harder than granting it the first time. A role with everything ticked is effectively a
          superadmin without the audit-log warnings; do not create one.
        </p>

        <h3>3.1 What each action verb means</h3>
        <p>
          The same verb behaves the same way on every resource it appears on, so once you know the
          verb you know what the box does.
        </p>
        <ul>
          <li><strong><code>create</code></strong> — add new records of this kind. (Without it, the
            "+ New" button is hidden.)</li>
          <li><strong><code>read</code></strong> — view records of this kind that fall within the
            holder's scope. Without it, the screen itself is hidden — they don't even see the
            menu entry.</li>
          <li><strong><code>update</code></strong> — edit existing records. Always paired with
            <code>read</code> in practice — there's nothing to edit if you can't see it.</li>
          <li><strong><code>delete</code></strong> — hard-delete records. We restrict this to
            superadmins for almost everything; granting it on a role is unusual and worth a second
            thought. Operational records (cases, intel) are normally <em>closed/redacted</em>, not
            deleted.</li>
          <li><strong><code>publish</code></strong> — only on <code>intel</code>. Lets the user
            post a new intel item to the feed.</li>
          <li><strong><code>export</code></strong> — only on <code>case</code> and <code>report</code>.
            Lets the user download data as CSV. Pair with care — exported files leave the system.</li>
          <li><strong><code>generate</code></strong> — only on <code>report</code>. Lets the user
            run report queries (i.e. open the Reports dashboards). <code>report.generate</code> is
            for analysts; <code>report.export</code> is what they need to take the data out.</li>
          <li>
            <strong><code>read_classified</code></strong> — only on <code>case</code>. Cases marked
            Restricted or Secret are hidden from anyone with plain <code>case.read</code>; they
            need this in addition to see them. Treat it like a clearance level.
          </li>
        </ul>

        <h3>3.2 What each resource means</h3>
        <p>
          Think of a resource as a "kind of record". The same actions are available on each;
          which combinations exist for each resource is shown by the boxes in the role editor.
        </p>
        <table className="guide-table">
          <thead>
            <tr><th>Resource</th><th>What it controls</th><th>Typical holders</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><code>case</code></td>
              <td>Investigation cases on the Cases page.</td>
              <td>Investigators, Branch Managers</td>
            </tr>
            <tr>
              <td><code>intel</code></td>
              <td>Intel feed — alerts, observations, notes.</td>
              <td>Officers</td>
            </tr>
            <tr>
              <td><code>poi</code></td>
              <td>People of Interest watchlist.</td>
              <td>Officers, Branch Managers</td>
            </tr>
            <tr>
              <td><code>task</code></td>
              <td>Task assignments people give each other.</td>
              <td>Most active users</td>
            </tr>
            <tr>
              <td><code>performance</code></td>
              <td>Performance reports submitted by branches.</td>
              <td>Branch Managers, Officers</td>
            </tr>
            <tr>
              <td><code>popularity</code></td>
              <td>Popularity reports submitted by branches.</td>
              <td>Branch Managers, Officers</td>
            </tr>
            <tr>
              <td><code>report</code></td>
              <td>Reports dashboards & CSV exports — <em>not</em> operational data, the
                aggregated/analytic side.</td>
              <td>Managers, Analysts</td>
            </tr>
            <tr>
              <td><code>audit</code></td>
              <td>Audit log — who did what, when.</td>
              <td>Auditors, Superadmins</td>
            </tr>
            <tr>
              <td><code>pii</code></td>
              <td>Reveal masked personal info (names, ID numbers, phone numbers) across every
                screen. Exactly one action: <code>read</code>.</td>
              <td>Investigators with a need-to-know</td>
            </tr>
            <tr>
              <td><code>constituency</code> / <code>ward</code> / <code>branch</code></td>
              <td>The territory hierarchy admin pages. Granting <code>create</code>/
                <code>update</code>/<code>delete</code> here lets the holder reshape the org map.</td>
              <td>Superadmins (mostly)</td>
            </tr>
            <tr>
              <td><code>user</code></td>
              <td>User accounts admin (create, deactivate, reset passwords).</td>
              <td>Superadmins</td>
            </tr>
            <tr>
              <td><code>role</code></td>
              <td>Role definitions and their permission sets.</td>
              <td>Superadmins only — granting this lets a holder grant themselves anything.</td>
            </tr>
            <tr>
              <td><code>position</code></td>
              <td>Org-chart titles. Metadata only.</td>
              <td>Superadmins</td>
            </tr>
            <tr>
              <td><code>assignment</code></td>
              <td>Who holds which role at which scope.</td>
              <td>Superadmins — same warning as <code>role</code>.</td>
            </tr>
            <tr>
              <td><code>screen</code></td>
              <td>UI screens & navigation menu config.</td>
              <td>Superadmins</td>
            </tr>
          </tbody>
        </table>

        <h3>3.3 Permissions to grant sparingly</h3>
        <p>
          The following are high-leverage. Read each twice before ticking it on a role. They show
          up everywhere the holder logs in — there is no per-record consent prompt.
        </p>
        <ul>
          <li>
            <strong><code>pii.read</code></strong> — unmasks personally identifiable info on every
            screen. A user with this can see real names, ID numbers, and phone numbers across
            every case and POI in their scope, not just the one they're investigating. Grant per
            person, not per role. If you can avoid it, do.
          </li>
          <li>
            <strong><code>case.read_classified</code></strong> — the Restricted/Secret clearance
            for cases. Granting this on a wide role gives the holders access to every classified
            case in their scope — the system has no per-case ACL beyond classification + scope.
          </li>
          <li>
            <strong><code>*.delete</code></strong> — almost always wrong on a non-superadmin role.
            Operational records should be closed/redacted; <em>delete</em> erases. Only the
            audit log retains the before-state.
          </li>
          <li>
            <strong><code>audit.read</code></strong> — auditors only. The audit log shows who
            accessed what; granting this to operations roles creates a self-surveillance feedback
            loop that defeats the audit's value.
          </li>
          <li>
            <strong><code>role.*</code></strong>, <strong><code>assignment.*</code></strong>,
            <strong><code>user.*</code></strong> — anyone with these can grant themselves any other
            permission. They are effectively superadmin under another name. Keep on the
            superadmin role only.
          </li>
          <li>
            <strong><code>report.export</code></strong> — every export creates a CSV that lives
            outside the platform. Grant only to people who actually need to take data out (e.g.
            for board reports), not to everyone who looks at dashboards.
          </li>
        </ul>

        <h3>3.4 How permissions, scope, and the reports-to chain interact</h3>
        <p>
          A user can do <em>X</em> on a record if and only if all three of these hold:
        </p>
        <ol>
          <li>They hold a permission for the action (e.g. <code>case.update</code>).</li>
          <li>The record's branch (or its parent ward / constituency) is inside their assignment's
            scope.</li>
          <li>For task assignments specifically: the recipient is at-or-below the actor in the
            <em>reports-to</em> chain — juniors can't task seniors.</li>
        </ol>
        <p>
          Strip any one of those and the action is refused with a 403. So granting a sweeping role
          to a junior is safe-ish if their scope is a single branch — they can only do the thing
          on that one branch. Conversely, a tightly-permissioned role at a constituency-level
          scope still gives them visibility across the whole constituency.
        </p>

        <h3>3.5 Suggested starting roles</h3>
        <p>
          You don't have to use these names — they're a sane starting point.
        </p>
        <ul>
          <li>
            <strong>Officer</strong> — <code>intel.read</code>, <code>intel.publish</code>,
            <code>case.read</code>, <code>poi.read</code>, <code>task.read</code>,
            <code>task.update</code>. Reads the feed, posts intel, sees cases and POI in their
            scope, can complete tasks assigned to them.
          </li>
          <li>
            <strong>Branch Manager</strong> — Officer + <code>case.create</code>,
            <code>case.update</code>, <code>poi.create</code>, <code>poi.update</code>,
            <code>task.create</code>, <code>performance.create</code>,
            <code>popularity.create</code>. Can open cases, manage the watchlist, assign tasks
            downward, file branch reports.
          </li>
          <li>
            <strong>Investigator</strong> — Branch Manager + <code>case.read_classified</code>,
            <code>pii.read</code>. Cleared to see Restricted/Secret cases and unmask PII in
            their scope.
          </li>
          <li>
            <strong>Analyst</strong> — <code>case.read</code>, <code>intel.read</code>,
            <code>poi.read</code>, <code>report.generate</code>, <code>report.export</code>.
            Read-only on operations, full access to Reports.
          </li>
          <li>
            <strong>Auditor</strong> — <code>audit.read</code> only. Read-only oversight; doesn't
            see operational data.
          </li>
        </ul>
        <p>
          Build these once on a fresh install, then assign them. Edit the bundled set when your
          org's needs change rather than spawning new roles for each variation.
        </p>

        <h2>4. Day-to-day operations</h2>

        <h3>4.1 Intel feed</h3>
        <p>
          Officers publish observations, alerts and notes. Each item has a kind (Note /
          Observation / Alert), severity (Low → Critical), and classification (Unclassified /
          Restricted / Secret). Items above Medium severity push notifications to the chain of
          command.
        </p>
        <p>
          Redaction: any officer with <code>intel.publish</code> can redact an item by giving a
          reason. Redaction is one-way — original text is hidden from everyone except auditors.
        </p>

        <h3>4.2 Cases</h3>
        <p>
          Investigation case files. Workflow: <em>Open → In review → Closed</em>. Cases can be
          re-opened. Subject details (name, ID number, phone) are PII-masked on every screen
          unless the viewer holds <code>pii.read</code>.
        </p>
        <p>
          Closing isn't deletion — the full history is kept. Only superadmins see a Delete button,
          and the audit log keeps the record.
        </p>

        <h3>4.3 People of Interest (POI)</h3>
        <p>
          A watchlist. Each entry has a threat level (Low / Medium / High), a status (Active /
          Dormant / Cleared), a classification, and a scope (Constituency / Ward / Branch). High
          threat additions push notifications upward.
        </p>

        <h3>4.4 Performance & Popularity reports</h3>
        <p>
          Branch officers submit numeric snapshots. The Reports module aggregates them. Failing
          performance scores and very-low/very-high popularity readings push notifications.
        </p>

        <h3>4.5 Map</h3>
        <p>
          <strong>Reports → Map.</strong> Geographic view of operational activity, plotted at one of three
          levels (constituency / ward / branch) using the lat/lng captured on each hierarchy entry. The map
          is bounded to Zambia and offers three free tile bases (Streets / Terrain / Satellite) via the
          control on the top-right.
        </p>
        <p>
          Pick the data source from the tabs at the top (Cases, Intel, POI, Tasks, Performance, Popularity)
          — only one at a time so layers don't overshadow each other. Then narrow with the severity /
          threat / priority chips. Click any bubble for the underlying records, with each one linking to
          its detail page. Bubble size scales with the count; colour follows the active filter or the
          dominant bucket.
        </p>

        <h3>4.6 Tasks</h3>
        <p>
          People assign tasks to others, with a priority (Low → Urgent), due date, and optional
          link to a case / intel item / POI. Rule of the chain: a junior can't assign upward.
          The system walks the reports-to chain to enforce this.
        </p>
        <p>
          Notifications: assigning a task or marking it overdue pushes to the recipient's device.
        </p>

        <h2>5. Maintaining the system</h2>

        <h3>5.1 When someone leaves</h3>
        <ol>
          <li>Go to <strong>Users</strong> → <strong>Deactivate</strong>. They're signed out
            immediately and can't sign back in.</li>
          <li>Their data stays — historical cases, intel and tasks reference them by UID.</li>
          <li>If they return, click <strong>Reactivate</strong>.</li>
        </ol>

        <h3>5.2 Forgotten password</h3>
        <p>
          Click <strong>Reset password</strong> on the user's row. The system generates a one-time
          link — share it with the user via a private channel. The current password keeps working
          until they use the link.
        </p>

        <h3>5.3 Promoting / demoting</h3>
        <p>
          Add or remove assignments via <strong>Users → Edit access</strong> rather than editing roles. The
          role definition affects every holder — the assignment affects only one person.
        </p>

        <h3>4.4 Audit trail</h3>
        <p>
          <strong>Insights → Audit log</strong>. Every create / update / delete / sign-in /
          permission change writes a record with actor, target, before/after, and timestamp. Audit
          records are exported nightly to Cloud Storage with locked retention (you can't tamper
          with them after they're written).
        </p>

        <h3>4.5 Reports & exports</h3>
        <p>
          <strong>Insights → Reports</strong>. Summary tab shows charts; the Raw data tab shows
          the underlying tables. <strong>Export CSV</strong> downloads the case records you can
          see (confirmation prompt + audit log entry every time).
        </p>

        <h2>5. Safety rails</h2>
        <ul>
          <li>
            <strong>Default-deny.</strong> Every API call requires a permission. If a user lacks
            it, the API returns 403 — the UI hides the screen too.
          </li>
          <li>
            <strong>Encryption.</strong> PII at rest is encrypted with AES-256-GCM and per-record
            keys wrapped by Cloud KMS. Even a stolen Firestore export is unreadable without the
            KMS key.
          </li>
          <li>
            <strong>Geo-fence.</strong> The platform is restricted to Zambia. Sign-in attempts
            from outside ZM are blocked at the edge.
          </li>
          <li>
            <strong>App Check.</strong> Web and PWA traffic must come from an attested browser.
            Calls from scripts or off-platform clients are rejected.
          </li>
          <li>
            <strong>Confirm prompts.</strong> Every destructive or visible action (close case,
            redact intel, deactivate user, export CSV, save role permissions) shows a "are you
            sure" with the consequence written out. Read it before clicking OK.
          </li>
        </ul>

        <h2>6. Common pitfalls</h2>
        <ul>
          <li>
            <strong>"My new user can't see anything."</strong> They have no assignment yet. Go to
            <strong>Users → Edit access</strong> and give them a role at a scope.
          </li>
          <li>
            <strong>"The Constituency dropdown is empty."</strong> Either you haven't created any
            constituencies (do that in Hierarchy first), or the user is non-superadmin and isn't
            assigned to any constituency yet.
          </li>
          <li>
            <strong>"I ticked the wrong permission boxes when creating a role."</strong> Open the
            role on the Roles page, click <strong>Edit permissions</strong>, untick or tick what
            you need, click Save. The change applies to every holder on next session refresh.
          </li>
          <li>
            <strong>"I want to delete a constituency that has wards."</strong> Move or delete the
            wards first. The hierarchy refuses to leave orphaned children.
          </li>
          <li>
            <strong>"The role ID box rejects spaces."</strong> Just type whatever you like — the
            preview line shows the slugified ID that will actually be saved (e.g. "Snake Case" →
            <code>snake_case</code>). Or leave the ID blank entirely to auto-generate it from the
            display name.
          </li>
        </ul>

        <h2>7. Emergency procedures</h2>
        <ul>
          <li>
            <strong>Suspected leak / compromised account.</strong> Go to <strong>Users</strong>,
            click <strong>Deactivate</strong> on the suspect account. They're signed out
            instantly. Then check the <strong>Audit log</strong> for what they accessed.
          </li>
          <li>
            <strong>Lost superadmin access.</strong> Only another superadmin can grant it back.
            If there's only one superadmin and they're locked out, raise a ticket with the
            project owner — they can grant superadmin via the Firebase console using the
            <code>setCustomUserClaims</code> SDK.
          </li>
          <li>
            <strong>Mistaken bulk action.</strong> Records are append-only. The audit log keeps
            the before/after of every change so you can tell exactly what changed and roll back
            by hand.
          </li>
        </ul>

        <h2>8. Reference</h2>
        <table className="guide-table">
          <thead>
            <tr><th>Page</th><th>Path</th><th>Who needs it</th></tr>
          </thead>
          <tbody>
            <tr><td>Hierarchy</td><td>/app/admin/hierarchy</td><td>Superadmin (mostly)</td></tr>
            <tr><td>Users</td><td>/app/admin/users</td><td>Superadmin</td></tr>
            <tr><td>Roles</td><td>/app/admin/roles</td><td>Superadmin</td></tr>
            <tr><td>Positions</td><td>/app/admin/positions</td><td>Superadmin</td></tr>
            <tr><td>People</td><td>/app/admin/people</td><td>Superadmin (community contacts, no platform access)</td></tr>
            <tr><td>Organogram</td><td>/app/admin/organogram</td><td>Superadmin (reports-to chain visualisation)</td></tr>
            <tr><td>Map</td><td>/app/map</td><td>Anyone with operational permissions (geographic view)</td></tr>
            <tr><td>Cases</td><td>/app/cases</td><td>Officers, Investigators</td></tr>
            <tr><td>Intel feed</td><td>/app/intel</td><td>Officers</td></tr>
            <tr><td>POI</td><td>/app/poi</td><td>Officers</td></tr>
            <tr><td>Tasks</td><td>/app/tasks</td><td>Everyone</td></tr>
            <tr><td>Performance reports</td><td>/app/performance</td><td>Branches</td></tr>
            <tr><td>Popularity reports</td><td>/app/popularity</td><td>Branches</td></tr>
            <tr><td>Reports & exports</td><td>/app/reports</td><td>Managers, Auditors</td></tr>
            <tr><td>Audit log</td><td>/app/audit</td><td>Auditors, Superadmin</td></tr>
          </tbody>
        </table>

        <p className="guide-footer muted">
          End of guide. If something here is wrong or out of date, say so — it'll be fixed and
          re-issued.
        </p>
      </article>
    </div>
  );
}
